/**
 * Normies API client.
 *
 * JSON data fetches go through the backend CORS proxy.
 * Image URLs are direct (no proxy needed for <img> tags).
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const PROXY_PREFIX = `${BACKEND_URL}/api/normies/proxy`;
const FETCH_TIMEOUT = 15000; // 15 seconds

/**
 * Fetch JSON from the Normies API via our backend proxy.
 * Returns parsed JSON or null on failure.
 */
async function proxyFetch(path) {
  const url = `${PROXY_PREFIX}${path}`;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT);

  try {
    const res = await fetch(url, { signal: controller.signal });
    clearTimeout(timer);
    if (!res.ok) {
      if (res.status === 404) return null;
      console.warn(`Normies proxy ${res.status}: ${path}`);
      return null;
    }
    return await res.json();
  } catch (err) {
    clearTimeout(timer);
    if (err.name === 'AbortError') {
      console.warn(`Normies proxy timeout: ${path}`);
    } else {
      console.warn(`Normies proxy error: ${path}`, err.message);
    }
    return null;
  }
}

/**
 * Fetch traits for a token.
 * @param {string|number} tokenId
 * @returns {Promise<Object|null>} Traits object or null
 */
export async function fetchTraits(tokenId) {
  return proxyFetch(`/normie/${tokenId}/traits`);
}

/**
 * Fetch canvas info for a token.
 * @param {string|number} tokenId
 * @returns {Promise<Object|null>}
 */
export async function fetchCanvasInfo(tokenId) {
  return proxyFetch(`/normie/${tokenId}/canvas/info`);
}

/**
 * Fetch canvas diff for a token.
 * @param {string|number} tokenId
 * @returns {Promise<Object|null>}
 */
export async function fetchCanvasDiff(tokenId) {
  return proxyFetch(`/normie/${tokenId}/canvas/diff`);
}

/**
 * Fetch full NFT metadata for a token.
 * @param {string|number} tokenId
 * @returns {Promise<Object|null>}
 */
export async function fetchMetadata(tokenId) {
  return proxyFetch(`/normie/${tokenId}/metadata`);
}

/**
 * Fetch agent binding for a token (or null if not bound).
 * @param {string|number} tokenId
 * @returns {Promise<Object|null>}
 */
export async function fetchAgentBinding(tokenId) {
  return proxyFetch(`/agents/binding/${tokenId}`);
}

/**
 * Fetch rich agent persona info for a token.
 * @param {string|number} tokenId
 * @returns {Promise<Object|null>}
 */
export async function fetchAgentInfo(tokenId) {
  return proxyFetch(`/agents/info/${tokenId}`);
}

/**
 * Fetch Normies collection history stats (burns, transforms, etc.)
 * @returns {Promise<Object|null>}
 */
export async function fetchHistoryStats() {
  return proxyFetch('/history/stats');
}

/**
 * Fetch OpenSea market stats for the Normies collection.
 * Goes through our backend OpenSea route (not the Normies proxy).
 * @returns {Promise<Object|null>}
 */
export async function fetchMarketStats() {
  const url = `${BACKEND_URL}/api/normies/market/stats`;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT);

  try {
    const res = await fetch(url, { signal: controller.signal });
    clearTimeout(timer);
    if (!res.ok) {
      console.warn(`Market stats ${res.status}`);
      return null;
    }
    return await res.json();
  } catch (err) {
    clearTimeout(timer);
    console.warn('Market stats error:', err.message);
    return null;
  }
}

/**
 * Fetch the top sale (highest price) from the last 24 hours.
 * Returns { topSale: { tokenId, priceHuman, symbol, ... }, salesCount } or null.
 * @returns {Promise<Object|null>}
 */
export async function fetchTopSale() {
  const url = `${BACKEND_URL}/api/normies/market/top-sale`;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT);

  try {
    const res = await fetch(url, { signal: controller.signal });
    clearTimeout(timer);
    if (!res.ok) {
      console.warn(`Top sale ${res.status}`);
      return null;
    }
    return await res.json();
  } catch (err) {
    clearTimeout(timer);
    console.warn('Top sale error:', err.message);
    return null;
  }
}


/**
 * Fetch burn history for a specific token (burns received by this token).
 * @param {string|number} tokenId
 * @returns {Promise<Object|null>}
 */
export async function fetchBurnHistory(tokenId) {
  return proxyFetch(`/history/burns/receiver/${tokenId}`);
}

/**
 * Fetch burned token info.
 * @param {string|number} tokenId
 * @returns {Promise<Object|null>}
 */
export async function fetchBurnedToken(tokenId) {
  return proxyFetch(`/history/burned/${tokenId}`);
}

/**
 * Aggregated fetch — loads all data needed for Normie Card and Agent Card modes.
 * Returns a combined data object or null if the token doesn't exist.
 *
 * @param {string|number} tokenId
 * @returns {Promise<Object|null>}
 */
export async function fetchNormieData(tokenId) {
  // Fetch traits first — if 404, token doesn't exist
  const traits = await fetchTraits(tokenId);
  if (!traits) return null;

  // Fetch remaining data in parallel
  const [canvasInfo, canvasDiff, agentBinding] = await Promise.all([
    fetchCanvasInfo(tokenId),
    fetchCanvasDiff(tokenId),
    fetchAgentBinding(tokenId),
  ]);

  // If agent is bound, fetch rich agent info
  let agentInfo = null;
  if (agentBinding) {
    agentInfo = await fetchAgentInfo(tokenId);
  }

  return {
    tokenId: String(tokenId),
    traits,
    canvasInfo,
    canvasDiff,
    agentBinding,
    agentInfo,
  };
}
